/// <amd-module name="Build/Extensions/GitHub/ConnectionSelection" />
import * as React from "react";

import { Actions, IServiceConnectionRetrieverService, ServiceConnectionInfo } from "Build/Extensions/Connection-Services/ServiceConnectionRetrieverService";
import { ConnectionList } from "Build/Extensions/GitHub/ConnectionList";
import * as Resources from "Build/Extensions/GitHub/Resources";
import { Constant, IServiceConnectionCreationService } from "Build/Extensions/Connection-Services/ServiceConnectionCreationService";

import * as DesignerResources from "Build/Designer/Resources";
import { IInstallAppService } from "Build/Common/Library/InstallAppService";

import { ITfsPageService } from "Tfs/Platform/Page";

import { format } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";

import { Button } from "VSSUI/Button";

export interface Props {
    serviceName: string;
    sourceProvider: string;
    onSelectConnection: (serviceConnectionId: string) => void;
    onErrorMessage: (errorMessage: string) => void;
}

interface State {
    showConnections: boolean;
    serviceConnections: ServiceConnectionInfo[];
    projectId: string;
}

export class ConnectionSelection extends VssComponent<Props, State, IVssComponentContext> {
    constructor(props: Props, context: IVssComponentContext) {
        super(props, context);

        const pageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const pageData = pageService.getData();

        this.state = {
            showConnections: false,
            serviceConnections: [],
            projectId: pageData!.project!.id,
        };
    }

    public componentDidMount(): void {
        const serviceConnectionRetrieverService = this.context.pageContext.getService<IServiceConnectionRetrieverService>("IServiceConnectionRetrieverService");
        serviceConnectionRetrieverService.subscribe(this._handleConnectionsUpdated, Actions.getServiceConnections);
        serviceConnectionRetrieverService.subscribe(this._handleError, Actions.error);
        serviceConnectionRetrieverService.updateServiceConnections(this.props.sourceProvider);
    }

    public componentWillUnmount(): void {
        const serviceConnectionRetrieverService = this.context.pageContext.getService<IServiceConnectionRetrieverService>("IServiceConnectionRetrieverService");
        serviceConnectionRetrieverService.unsubscribe(this._handleConnectionsUpdated, Actions.getServiceConnections);
        serviceConnectionRetrieverService.unsubscribe(this._handleError, Actions.error);
    }

    public render(): JSX.Element {
        const hasConnections: boolean = (this.state && this.state.serviceConnections && this.state.serviceConnections.length > 0);
        return <div className="github-connection-selection">
            {
                <div className="header-section">
                    <div className="subheader body-m secondary-text">{DesignerResources.NewPipeline}</div>
                    <div className="header title-l">{Resources.SelectRepositoryTitle}</div>
                </div>
            }
            {
                hasConnections &&
                <div className="section">
                    <div className="section-header title-s">{Resources.UseExistingConnection}</div>
                    <ConnectionList
                        showConnections={this.state.showConnections}
                        serviceConnections={this.state.serviceConnections}
                        getItemPrimaryText={this._getItemPrimaryText}
                        getItemSecondaryText={this._getItemSecondaryText}
                        onSelectConnection={this.props.onSelectConnection} />
                </div>
            }
            {
                this.state.showConnections && !hasConnections &&
                <AccessTokenAcquirer
                    serviceName={this.props.serviceName}
                    hasExistingConns={hasConnections}
                    createAuthRequest={this._createAuthRequest} />
            }
            {
                this.state.showConnections &&
                this.renderAppInstall()
            }
        </div>;
    }

    private renderAppInstall = (): JSX.Element => {
        return (
            <div className="section">
                <div className="section-header title-s">{Resources.InstallGitHubAppHeader}</div>
                <div className="github-description body-m">{Resources.InstallGitHubAppDescription}</div>
                <Button
                    className="install-button"
                    text={Resources.InstallGitHubApp}
                    onClick={this._installApp}
                />
                <div className="github-footer body-m">{Resources.InstallGitHubAppFooter}</div>
            </div>
        );
    }

    private _getItemPrimaryText = (connection: ServiceConnectionInfo): string => {
        if (connection) {
            return connection.name
        }
        return Resources.Unknown;
    }

    private _getItemSecondaryText = (connection: ServiceConnectionInfo): string => {
        if (connection && connection.scheme) {
            if (connection.scheme === Constant.TokenScheme) {
                return Resources.GitHubApp;
            } else if (connection.scheme === Constant.PersonalAccessTokenScheme) {
                return Resources.PersonalAccessToken;
            } else {
                return connection.scheme;
            }
        }

        return Resources.Unknown;
    }

    private _handleConnectionsUpdated = (serviceConnections: ServiceConnectionInfo[]): void => {
        this.setState({
            showConnections: true,
            serviceConnections: serviceConnections,
        });
    }

    private _installApp = (): void => {
        const installAppService = this.context.pageContext.getService<IInstallAppService>("IInstallAppService");
        installAppService.installApp();
    }

    private _createAuthRequest = () => {
        const service = this.context.pageContext.getService<IServiceConnectionCreationService>("IServiceConnectionCreationService");
        service.createAuthRequest(this.context, this.props.sourceProvider, this.state.projectId, false, this._handleError);
    }

    private _handleError = (e: any) => {
        if (e && e.message) {
            this.props.onErrorMessage(e.message);
        } else {
            this.props.onErrorMessage(e);
        }
    }
}

interface AccessTokenAcquirerProps {
    serviceName: string;
    hasExistingConns: boolean;
    createAuthRequest: () => void;
}

class AccessTokenAcquirer extends React.Component<AccessTokenAcquirerProps> {
    public render(): JSX.Element {
        return <div className="section">
            <div className="connection-subheader title-s">{format(Resources.GrantPermission, this.props.serviceName)}</div>
            {
                this.props.hasExistingConns
                    ? <Button
                        className="authorize-button"
                        text={Resources.Authorize}
                        onClick={this.props.createAuthRequest}
                    />
                    : <Button
                        className="authorize-button"
                        primary={true}
                        text={Resources.Authorize}
                        onClick={this.props.createAuthRequest}
                    />
            }
        </div>;
    }
}
